import { useCallback, useState } from 'react'
import { Upload, FileText, Image, X, Loader2 } from 'lucide-react'
import { useProcessorStore } from '../stores/processorStore'
import * as pdfjsLib from 'pdfjs-dist'

// Set worker source for pdf.js - use unpkg CDN which has proper CORS
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`

const MAX_PDF_SIZE = 48 * 1024 * 1024 // 48 MB
const MAX_IMAGE_SIZE = 20 * 1024 * 1024 // 20 MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png']
const ALLOWED_PDF_TYPE = 'application/pdf'

async function extractPdfText(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer()
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise

  const textParts: string[] = []

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i)
    const textContent = await page.getTextContent()
    const pageText = textContent.items
      .map((item) => ('str' in item ? item.str : ''))
      .join(' ')
    textParts.push(pageText)
  }

  return textParts.join('\n\n')
}

// Render PDF pages as images for vision APIs that don't handle embedded PDF images well
async function renderPdfPagesToImages(file: File): Promise<string[]> {
  const arrayBuffer = await file.arrayBuffer()
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
  const pageImages: string[] = []

  // Use 1.5x scale and lower JPEG quality to keep size reasonable
  // Target ~200-400KB per page to stay under API limits
  const scale = 1.5

  const numPages = pdf.numPages
  console.log(`[pdf] Rendering ${numPages} pages as images...`)

  for (let i = 1; i <= numPages; i++) {
    const page = await pdf.getPage(i)
    const viewport = page.getViewport({ scale })

    // Create canvas for rendering
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')!
    canvas.height = viewport.height
    canvas.width = viewport.width

    // Render page to canvas
    await page.render({
      canvasContext: context,
      viewport: viewport,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any).promise

    // Convert to base64 JPEG with moderate quality (0.7) to reduce size
    const imageData = canvas.toDataURL('image/jpeg', 0.7)
    const base64 = imageData.split(',')[1]
    pageImages.push(base64)

    const sizeKB = Math.round(base64.length / 1024)
    console.log(`[pdf] Page ${i}/${numPages}: ${sizeKB}KB`)
  }

  const totalSizeMB = pageImages.reduce((sum, img) => sum + img.length, 0) / 1024 / 1024
  console.log(`[pdf] Total page images: ${totalSizeMB.toFixed(2)}MB for ${numPages} pages`)

  return pageImages
}

export function FileUpload() {
  const {
    uploadedFile,
    isUploading,
    uploadError,
    setUploadedFile,
    setIsUploading,
    setUploadError,
    clearConversation,
  } = useProcessorStore()

  const [isDragging, setIsDragging] = useState(false)

  const validateFile = (file: File): string | null => {
    const isPdf = file.type === ALLOWED_PDF_TYPE
    const isImage = ALLOWED_IMAGE_TYPES.includes(file.type)

    if (!isPdf && !isImage) {
      return 'Invalid file type. Please upload a PDF, JPG, or PNG file.'
    }

    if (isPdf && file.size > MAX_PDF_SIZE) {
      return `PDF file too large. Maximum size is ${MAX_PDF_SIZE / 1024 / 1024}MB.`
    }

    if (isImage && file.size > MAX_IMAGE_SIZE) {
      return `Image file too large. Maximum size is ${MAX_IMAGE_SIZE / 1024 / 1024}MB.`
    }

    return null
  }

  const uploadFile = async (file: File) => {
    const validationError = validateFile(file)
    if (validationError) {
      setUploadError(validationError)
      return
    }

    setIsUploading(true)
    setUploadError(null)
    clearConversation()

    try {
      const isImage = ALLOWED_IMAGE_TYPES.includes(file.type)

      if (isImage) {
        // For images, convert to base64 and store in memory
        const reader = new FileReader()
        reader.onload = () => {
          const base64 = (reader.result as string).split(',')[1]
          setUploadedFile({
            id: `local_${Date.now()}`,
            name: file.name,
            type: 'image',
            size: file.size,
            imageBase64: base64, // Store in Zustand (memory) - sessionStorage has 5MB limit
          })
          setIsUploading(false)
        }
        reader.onerror = () => {
          setUploadError('Failed to read image file')
        }
        reader.readAsDataURL(file)
      } else {
        // For PDFs, extract text, store raw base64, AND render pages as images
        let pageImages: string[] = []

        const [pdfText, pdfBase64] = await Promise.all([
          extractPdfText(file),
          new Promise<string>((resolve) => {
            const reader = new FileReader()
            reader.onload = () => {
              const base64 = (reader.result as string).split(',')[1]
              resolve(base64)
            }
            reader.readAsDataURL(file)
          })
        ])

        // Render pages separately with error handling
        try {
          console.log('[pdf] Starting page rendering...')
          pageImages = await renderPdfPagesToImages(file)
          console.log(`[pdf] Rendered ${pageImages.length} pages, total: ${Math.round(pageImages.reduce((s,i) => s + i.length, 0) / 1024)}KB`)
        } catch (renderErr) {
          console.error('[pdf] Page rendering failed:', renderErr)
          // Continue without page images - will fall back to PDF
        }

        console.log(`[pdf] Extracted ${pdfText.length} chars text, ${pageImages.length} page images`)

        setUploadedFile({
          id: `local_${Date.now()}`,
          name: file.name,
          type: 'pdf',
          size: file.size,
          pdfText: pdfText || '[No text extracted - scanned PDF]',
          pdfBase64, // Raw PDF for native vision APIs
          pageImages, // Rendered page images for fallback vision
        })
        setIsUploading(false)
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed')
      setIsUploading(false)
    }
  }

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)

      const file = e.dataTransfer.files[0]
      if (file) {
        uploadFile(file)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        uploadFile(file)
      }
      // Reset input so same file can be selected again
      e.target.value = ''
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleRemoveFile = useCallback(() => {
    setUploadedFile(null)
    clearConversation()
  }, [setUploadedFile, clearConversation])

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  }

  if (uploadedFile) {
    return (
      <div className="bg-gray-800 rounded-lg p-4 flex items-center gap-4">
        <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center">
          {uploadedFile.type === 'pdf' ? (
            <FileText className="w-6 h-6 text-red-400" />
          ) : (
            <Image className="w-6 h-6 text-blue-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{uploadedFile.name}</p>
          <p className="text-sm text-gray-400">
            {uploadedFile.type.toUpperCase()} • {formatFileSize(uploadedFile.size)}
            {uploadedFile.pdfText && ` • ${uploadedFile.pdfText.length.toLocaleString()} chars`}
            {uploadedFile.pageImages && uploadedFile.pageImages.length > 0 ? (
              <span className="text-green-400"> • {uploadedFile.pageImages.length} page images ✓</span>
            ) : (
              <span className="text-yellow-400"> • [v2] No page images</span>
            )}
          </p>
        </div>
        <button
          onClick={handleRemoveFile}
          className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          title="Remove file"
        >
          <X className="w-5 h-5 text-gray-400" />
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <label
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          block border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
          transition-colors duration-200
          ${isDragging ? 'border-blue-500 bg-blue-500/10' : 'border-gray-700 hover:border-gray-600'}
          ${isUploading ? 'pointer-events-none opacity-50' : ''}
        `}
      >
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleFileInput}
          className="hidden"
          disabled={isUploading}
        />

        {isUploading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
            <p className="text-gray-400">Processing file...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <Upload className="w-10 h-10 text-gray-500" />
            <div>
              <p className="text-gray-300">
                Drop your file here or <span className="text-blue-400">browse</span>
              </p>
              <p className="text-sm text-gray-500 mt-1">
                PDF (max 48MB) or Images (max 20MB)
              </p>
            </div>
          </div>
        )}
      </label>

      {uploadError && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm">
          {uploadError}
        </div>
      )}
    </div>
  )
}
